import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Divider,
  Heading,
  Icon,
  Link,
  Modal as TRModal,
  StackView,
  Text,
  Tooltip,
} from '@planningcenter/tapestry-react'
import { ThemeProvider } from '~/components/ThemeProvider'

function Modal({ open, onRequestClose }) {
  return (
    <TRModal
      id="modal"
      style={{ width: '640px', maxWidth: '640px' }}
      height="auto"
      open={open}
    >
      <StackView spacing={2}>
        <Heading color="grey-9" weight={600} level={2} size={1}>
          OAuth Application Types
        </Heading>

        <Text size={3} style={{ lineHeight: '1.65' }}>
          OAuth defines two types of applications: <strong>confidential</strong>{' '}
          and <strong>public</strong>.
        </Text>

        <Text size={3} style={{ lineHeight: '1.65' }}>
          Confidential applications are able to keep their registered{' '}
          <code>client_secret</code> safe and use it to authenticate with the
          authorization server (Planning Center). Generally, that requires
          requests for <code>access_tokens</code> to originate from a web server
          you own.
        </Text>

        <Text size={3} style={{ lineHeight: '1.65' }}>
          Public applications are unable to use a registered{' '}
          <code>client_secret</code>, usually because they're running in the
          browser or on a mobile device.
        </Text>

        <StackView
          axis="horizontal"
          spacing={1.5}
          alignment="start"
          padding={2}
          background="hsla(203, 94%, 94%, 1)"
          radius={4}
          marginBottom={1}
        >
          <Icon
            name="general.infoCircle"
            color="hsla(204, 100%, 40%, 1)"
            size="lg"
            marginTop={0.25}
          />

          <Text size={4} style={{ lineHeight: '1.5' }} color="grey-8">
            We recommend you create a confidential OAuth application, you issue
            authorization requests from a server where you can keep your{' '}
            <code>client_secret</code> safe, and you utilize the PKCE extension.
            This is the most secure setup for both you and our shared users.
          </Text>
        </StackView>
        <Box>
          <Heading
            color="grey-9"
            weight={600}
            level={3}
            size={3}
            marginBottom={0.5}
          >
            How does this relate to the PKCE extension?
          </Heading>

          <Text size={3} style={{ lineHeight: '1.65' }}>
            Both confidential and public OAuth applications can use the{' '}
            <Link to="https://oauth.net/2/pkce/" external>
              PKCE
            </Link>{' '}
            extension. However, only public OAuth applications will be able omit
            a <code>client_secret</code> in their authorization requests.
          </Text>
        </Box>

        <Divider marginTop={2} />

        <StackView
          axis="horizontal"
          distribution="end"
          marginVertical={1}
          spacing={1}
        >
          <Button
            title="OK"
            theme="interaction"
            variant="fill"
            size="lg"
            onClick={onRequestClose}
            fontWeight={500}
          />
        </StackView>
      </StackView>
    </TRModal>
  )
}

export default function ConfidentialVsPublic(props) {
  const [open, setOpen] = useState(false)

  return (
    <React.Fragment>
      <ThemeProvider>
        <Link onClick={() => setOpen(true)}>
          <Tooltip placement="top" openDelay={0}>
            <Icon name="general.outlinedQuestionCircle" size="md" />
          </Tooltip>
        </Link>
      </ThemeProvider>

      {createPortal(
        <ThemeProvider>
          <Modal open={open} onRequestClose={() => setOpen(false)} />
        </ThemeProvider>,
        document.body
      )}
    </React.Fragment>
  )
}

ConfidentialVsPublic.propTypes = {
  open: PropTypes.bool,
  onRequestClose: PropTypes.func,
}
